import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";

import { AuthContext } from "@contexts/AuthContext";
import ModelList from "@components/ModelList";
import ModelGrid from "@components/ModelGrid";
import ToggleDrawingTab from "@components/ToggleDrawingTab/ToggleDrawingTab";
import ModelListLayoutController from "@components/ModelListLayoutController/ModelListLayoutController";
import SearchBar from "@common-components/SearchBar/SearchBar";
import Spinner from "@common-components/Spinner/Spinner";

const ProjectPage = ({ onModelSelect }) => {
  const { currentUserRole } = useContext(AuthContext);
  const { projectName } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const [modelListLayoutStyle, setModelListLayoutStyle] = useState("list");
  const [searchKey, setSearchKey] = useState("");
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false);
  const excludedViewRef = useRef(null);

  useEffect(() => {
    if (!currentUserRole) {
      setSelectedTab("SALES");
    } else if (
      currentUserRole === "SALES" ||
      currentUserRole?.startsWith("SALES_") ||
      currentUserRole === "ADMIN" ||
      currentUserRole?.startsWith("ADMIN_") ||
      currentUserRole === "MANAGE" ||
      currentUserRole?.startsWith("MANAGE_")
    ) {
      setSelectedTab("SALES");
    } else if (
      currentUserRole === "SITE" ||
      currentUserRole?.startsWith("SITE_")
    ) {
      setSelectedTab("COORDINATION");
    }
  }, [currentUserRole]);

  // Check if project is selected and fetch project data from localStorage
  useEffect(() => {
    try {
      const storedProject = JSON.parse(
        localStorage.getItem("projectSelection"),
      );

      if (storedProject?.name === projectName) {
        setSelectedProject(storedProject);
      } else {
        navigate("/home");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }, [projectName, navigate]);

  // reset pdf-zoom-tip
  useEffect(() => {
    sessionStorage.setItem("aco-pdf-zoom-toast", "false");
  }, []);

  // Function to handle model selection
  const handleModelSelect = (model) => {
    onModelSelect(model);
    navigate(
      `/home/${selectedTab}/${projectName}/${model["name"]}?version=${model["selectedVersion"]}`,
    );
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleSwitchModelListLayoutStyle = (event, newValue) => {
    setModelListLayoutStyle(newValue);
  };

  const handleSearch = (event) => {
    setSearchKey(event.target.value);
  };

  return (
    <Box
      sx={(theme) => ({
        height: "80vh",
        padding: "20px 10%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "20px",
        [theme.breakpoints.down("lg")]: {
          height: "76vh",
          minHeight: "76vh",
        },
        [theme.breakpoints.down("md")]: {
          height: "70vh",
          minHeight: "70vh",
        },
      })}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              flexDirection: "row",
              justifyContent: "center",
              position: "relative",
              alignItems: "center",
              "@media (max-width: 1330px)": {
                flexDirection: "column",
                position: "unset",
              },
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: "primary.main",
                marginBottom: "7px",
                display: "block",
                position: "absolute",
                "@media (max-width: 1330px)": {
                  position: "unset",
                },
              }}
            >
              {selectedProject?.name.toUpperCase()}
            </Typography>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <ToggleDrawingTab
                currentUserRole={currentUserRole}
                selectedTab={selectedTab}
                handleTabChange={handleTabChange}
              />

              <SearchBar
                handleSearch={handleSearch}
                isSearchBarOpen={isSearchBarOpen}
                setIsSearchBarOpen={setIsSearchBarOpen}
                excludedViewRef={excludedViewRef}
              />

              <ModelListLayoutController
                modelListLayoutStyle={modelListLayoutStyle}
                handleSwitchModelListLayoutStyle={
                  handleSwitchModelListLayoutStyle
                }
              />
            </Box>
          </Box>

          {modelListLayoutStyle === "list" ? (
            <ModelList
              selectedTab={selectedTab}
              selectedProject={selectedProject}
              onModelSelect={handleModelSelect}
              searchKey={searchKey}
              excludedViewRef={excludedViewRef}
            />
          ) : modelListLayoutStyle === "grid" ? (
            <ModelGrid
              selectedTab={selectedTab}
              selectedProject={selectedProject}
              onModelSelect={handleModelSelect}
              searchKey={searchKey}
              excludedViewRef={excludedViewRef}
            />
          ) : null}
        </>
      )}
    </Box>
  );
};

export default ProjectPage;
